// Shared lazy-play behavior for any <video class="lazy-scroll-video">, sitewide.
// Sections mark a video with this class and drop `autoplay: false` — this
// script handles play-on-enter / pause-on-exit for all of them, so no
// section needs its own copy of this logic.
(function () {
  if (!('IntersectionObserver' in window)) return;

  // Maps the observed target element -> the video(s) it controls. Normally a
  // video observes itself, but a video can opt into a different trigger via
  // data-lazy-trigger="<selector>" — necessary for a video stretched across
  // a large absolutely-positioned background (like a multi-section scene),
  // where "some pixel of this giant box is on screen" fires the instant the
  // page loads, long before the content the video is actually paired with is
  // visible. Pointing at a small, precise element instead (e.g. a heading)
  // ties playback to when that specific content comes into view.
  var targetVideos = new Map();

  function setPlaying(video, shouldPlay) {
    if (shouldPlay) {
      var playPromise = video.play();
      if (playPromise !== undefined) {
        playPromise.catch(function () {});
      }
    } else {
      video.pause();
    }
  }

  function onIntersect(entries) {
    entries.forEach(function (entry) {
      var videos = targetVideos.get(entry.target) || [entry.target];
      videos.forEach(function (video) {
        setPlaying(video, entry.isIntersecting);
      });
    });
  }

  // Self-observing videos (no data-lazy-trigger, e.g. the About images) get a
  // head start so there's no visible "poster frame catching up" moment once
  // they're genuinely below the fold at page load.
  var selfObserver = new IntersectionObserver(onIntersect, {
    rootMargin: '200px 0px 200px 0px',
    threshold: 0,
  });

  // Split enter/exit trigger: for a video acting as the background for a
  // large area (e.g. an entire multi-section scene), a single shared trigger
  // can't do the right thing on both ends — a small precise element (like a
  // heading) is right for *starting* playback at the right moment, but using
  // that same small element to decide when to *stop* means the video pauses
  // the instant that heading scrolls away, even though it's still the
  // background for everything below it. These two observers split that
  // apart: entering the small start trigger only ever plays, exiting the
  // large stop trigger only ever pauses — neither one fires the opposite
  // action, so the video keeps playing across the whole area it's behind.
  var enterOnlyVideos = new Map();
  var exitOnlyVideos = new Map();

  var enterOnlyObserver = new IntersectionObserver(function (entries) {
    entries.forEach(function (entry) {
      if (!entry.isIntersecting) return;
      var videos = enterOnlyVideos.get(entry.target) || [];
      videos.forEach(function (video) { setPlaying(video, true); });
    });
  }, {
    rootMargin: '0px 0px -15% 0px',
    threshold: 0,
  });

  var exitOnlyObserver = new IntersectionObserver(function (entries) {
    entries.forEach(function (entry) {
      if (entry.isIntersecting) return;
      var videos = exitOnlyVideos.get(entry.target) || [];
      videos.forEach(function (video) { setPlaying(video, false); });
    });
  }, {
    rootMargin: '0px',
    threshold: 0,
  });

  function registerSplitTrigger(video, enterEl, exitEl) {
    if (!enterOnlyVideos.has(enterEl)) enterOnlyVideos.set(enterEl, []);
    enterOnlyVideos.get(enterEl).push(video);
    enterOnlyObserver.observe(enterEl);

    if (!exitOnlyVideos.has(exitEl)) exitOnlyVideos.set(exitEl, []);
    exitOnlyVideos.get(exitEl).push(video);
    exitOnlyObserver.observe(exitEl);
  }

  function registerVideo(video) {
    var triggerSelector = video.dataset.lazyTrigger;
    var stopTriggerSelector = video.dataset.lazyStopTrigger;

    if (triggerSelector) {
      var enterEl = document.querySelector(triggerSelector);
      if (enterEl) {
        if (stopTriggerSelector) {
          var exitEl = document.querySelector(stopTriggerSelector);
          if (exitEl) {
            registerSplitTrigger(video, enterEl, exitEl);
            return;
          }
        }
        // data-lazy-trigger with no stop trigger: play once when the start
        // trigger is reached, then never auto-pause again. The video's
        // download already happened by the time it starts, so there's no
        // real cost being avoided by pausing again on scroll-past — and for
        // a video acting as a large background, there's no single "small"
        // element whose exit should mean "stop" anyway.
        if (!enterOnlyVideos.has(enterEl)) enterOnlyVideos.set(enterEl, []);
        enterOnlyVideos.get(enterEl).push(video);
        enterOnlyObserver.observe(enterEl);
        return;
      }
    }

    // Self-observing videos (no data-lazy-trigger, e.g. the About images):
    // play/pause tied to the video's own visibility.
    if (!targetVideos.has(video)) {
      targetVideos.set(video, []);
    }
    targetVideos.get(video).push(video);
    selfObserver.observe(video);
  }

  function observeAll(root) {
    root.querySelectorAll('video.lazy-scroll-video').forEach(registerVideo);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      observeAll(document);
    });
  } else {
    observeAll(document);
  }

  // Shopify's theme editor (and any AJAX section re-render) can add new
  // lazy-scroll-video elements after the initial page load — watch for them
  // so a merchant editing a section in Customize doesn't lose the behavior.
  var mutationObserver = new MutationObserver(function (mutations) {
    mutations.forEach(function (mutation) {
      mutation.addedNodes.forEach(function (node) {
        if (node.nodeType !== 1) return;
        if (node.matches && node.matches('video.lazy-scroll-video')) {
          registerVideo(node);
        }
        if (node.querySelectorAll) {
          observeAll(node);
        }
      });
    });
  });
  mutationObserver.observe(document.documentElement, { childList: true, subtree: true });
})();

// Sitewide safety net: force-stop every playing video (and any YouTube/Vimeo
// embed) the instant a tab is backgrounded or navigated away from. Without
// this, a video started by a click (e.g. a UGC testimonial) keeps playing
// audibly in a background tab indefinitely, since browsers don't pause
// media just because a tab loses focus. This is independent of whatever
// started playback, so it catches every case, not just one component.
(function () {
  function stopAllMedia() {
    document.querySelectorAll('video').forEach(function (video) {
      if (!video.paused) video.pause();
    });
    // YouTube/Vimeo iframes have no direct pause() call available without
    // their embed APIs; reassigning the same src is the reliable way to
    // force-stop them (reloads the iframe, discarding playback state).
    document.querySelectorAll('iframe.external-video').forEach(function (iframe) {
      var src = iframe.getAttribute('src');
      if (src) iframe.setAttribute('src', src);
    });
  }

  document.addEventListener('visibilitychange', function () {
    if (document.hidden) stopAllMedia();
  });
  window.addEventListener('pagehide', stopAllMedia);
})();

// Sitewide gate: any *unmuted* video/audio must be started by a real,
// recent user gesture (click/tap/keypress), full stop — no matter which
// script tries to start it. This is what actually stops third-party widgets
// (e.g. a reviews/UGC app's own carousel, loaded from another domain) from
// making sound on their own timer, since it doesn't matter what triggers
// their play() call — it gets paused right back if it wasn't preceded by
// the visitor actually touching the page a moment ago.
//
// Muted playback is exempt on purpose: it can't produce sound, and it's
// exactly how our own hero/background videos are meant to autoplay
// (see the data-scroll-play videos above) — this only targets audio.
(function () {
  var lastUserGestureAt = 0;
  ['pointerdown', 'keydown'].forEach(function (type) {
    document.addEventListener(type, function () {
      lastUserGestureAt = Date.now();
    }, true);
  });

  document.addEventListener('play', function (e) {
    var el = e.target;
    if (!(el instanceof HTMLMediaElement) || el.muted) return;
    if (Date.now() - lastUserGestureAt > 1000) {
      el.pause();
    }
  }, true);
})();